import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { validateLlmPromptClientSide } from '../utils/promptValidation';
import { 
    CloudUpload, 
    Info, 
    ChevronDown, 
    Loader2, 
    ListVideo, 
    Lock,
    Sparkles,
    Cpu,
    Zap,
    ExternalLink,
    Check,
    AlertCircle,
    Play,
    Clock,
    User,
    Calendar,
    Settings,
    Wand2
} from 'lucide-react';
import { api } from '../api';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';

// Types
interface PlatformVod {
    id: string;
    user_name: string;
    title: string;
    url: string;
    thumbnail_url: string;
    duration: string;
    published_at: string;
    platform: 'Twitch' | 'Kick';
}

interface ModernVodUploadFormProps {
    initialSelectedDevice: 'CPU' | 'GPU';
    onDeviceChange: (newDevice: 'CPU' | 'GPU') => void;
    customLlmPrompt: string;
    setCustomLlmPrompt: (value: string) => void;
    clipCount: number;
    setClipCount: (value: number) => void;
    customPromptAllowed?: boolean;
    onBeforeUpload?: (isGpuAttempt: boolean) => Promise<boolean>;
    onAfterUpload?: (isGpuAttempt: boolean) => Promise<void>;
}

// Glassmorphism Input Component
const GlassInput: React.FC<{
    value: string;
    onChange: (value: string) => void;
    placeholder: string;
    disabled?: boolean;
    error?: string;
    icon?: React.ReactNode;
    className?: string;
}> = ({ value, onChange, placeholder, disabled, error, icon, className = '' }) => (
    <div className={`relative ${className}`}>
        <div className={`
            relative flex items-center
            bg-white/[0.02] backdrop-blur-xl border rounded-xl transition-all duration-200
            ${error 
                ? 'border-red-400/50 ring-1 ring-red-400/20' 
                : 'border-white/10 hover:border-white/20 focus-within:border-white/30 focus-within:ring-1 focus-within:ring-white/20'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
        `}>
            {icon && (
                <div className="absolute left-4 text-white/40">
                    {icon}
                </div>
            )}
            <input
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                className={`
                    w-full px-4 py-4 bg-transparent text-white placeholder-white/40
                    focus:outline-none disabled:cursor-not-allowed
                    ${icon ? 'pl-12' : ''}
                `}
            />
        </div>
        {error && (
            <p className="mt-2 text-sm text-red-400 flex items-center gap-2">
                <AlertCircle size={14} />
                {error}
            </p>
        )}
    </div>
);

// Glassmorphism Button Component
const GlassButton: React.FC<{
    children: React.ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    loading?: boolean;
    variant?: 'primary' | 'secondary' | 'success';
    size?: 'sm' | 'md' | 'lg';
    className?: string;
}> = ({ 
    children, 
    onClick, 
    disabled, 
    loading, 
    variant = 'primary', 
    size = 'md', 
    className = '' 
}) => {
    const variants = {
        primary: 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-400/30 text-blue-100 hover:from-blue-500/30 hover:to-cyan-500/30',
        secondary: 'bg-white/[0.02] border-white/10 text-white/70 hover:bg-white/[0.04] hover:border-white/20',
        success: 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-green-400/30 text-green-100 hover:from-green-500/30 hover:to-emerald-500/30'
    };

    const sizes = {
        sm: 'px-3 py-2 text-sm',
        md: 'px-6 py-3 text-base',
        lg: 'px-8 py-4 text-lg'
    };

    return (
        <button
            onClick={onClick}
            disabled={disabled || loading}
            className={`
                relative flex items-center justify-center gap-2 rounded-xl font-medium
                backdrop-blur-xl border transition-all duration-200
                disabled:opacity-50 disabled:cursor-not-allowed
                hover:shadow-lg hover:shadow-black/20
                ${variants[variant]}
                ${sizes[size]}
                ${className}
            `}
        >
            {loading && <Loader2 size={16} className="animate-spin" />}
            {children}
        </button>
    );
};

// Device Selection Component
const DeviceSelector: React.FC<{
    selected: 'CPU' | 'GPU';
    onChange: (device: 'CPU' | 'GPU') => void;
    subscription: any;
}> = ({ selected, onChange, subscription }) => {
    const hasGpuAccess = subscription?.tierId && [2, 3].includes(subscription.tierId);

    return (
        <div className="space-y-3">
            <label className="block text-sm font-medium text-white/70">
                Processing Device
            </label>
            <div className="grid grid-cols-2 gap-3">
                {/* CPU Option */}
                <div
                    onClick={() => onChange('CPU')}
                    className={`
                        relative p-4 rounded-xl border cursor-pointer transition-all duration-200
                        ${selected === 'CPU'
                            ? 'bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border-blue-400/50 ring-1 ring-blue-400/30'
                            : 'bg-white/[0.02] border-white/10 hover:border-white/20'
                        }
                    `}
                >
                    <div className="flex items-center gap-3">
                        <div className={`
                            p-2 rounded-lg 
                            ${selected === 'CPU' ? 'bg-blue-500/30' : 'bg-white/10'}
                        `}>
                            <Cpu size={16} className="text-white" />
                        </div>
                        <div>
                            <div className="font-medium text-white">CPU</div>
                            <div className="text-xs text-white/60">Standard processing</div>
                        </div>
                    </div>
                    {selected === 'CPU' && (
                        <div className="absolute top-2 right-2">
                            <Check size={16} className="text-blue-400" />
                        </div>
                    )}
                </div>

                {/* GPU Option */}
                <div
                    onClick={() => hasGpuAccess && onChange('GPU')}
                    className={`
                        relative p-4 rounded-xl border transition-all duration-200
                        ${!hasGpuAccess 
                            ? 'opacity-50 cursor-not-allowed bg-white/[0.01] border-white/5'
                            : selected === 'GPU'
                                ? 'bg-gradient-to-r from-emerald-500/20 to-teal-500/20 border-emerald-400/50 ring-1 ring-emerald-400/30 cursor-pointer'
                                : 'bg-white/[0.02] border-white/10 hover:border-white/20 cursor-pointer'
                        }
                    `}
                >
                    <div className="flex items-center gap-3">
                        <div className={`
                            p-2 rounded-lg 
                            ${selected === 'GPU' ? 'bg-emerald-500/30' : 'bg-white/10'}
                        `}>
                            <Zap size={16} className="text-white" />
                        </div>
                        <div>
                            <div className="font-medium text-white flex items-center gap-2">
                                GPU
                                {!hasGpuAccess && <Lock size={12} className="text-white/40" />}
                            </div>
                            <div className="text-xs text-white/60">
                                {hasGpuAccess ? 'Faster processing' : 'Tier 2+ required'}
                            </div>
                        </div>
                    </div>
                    {selected === 'GPU' && hasGpuAccess && (
                        <div className="absolute top-2 right-2">
                            <Check size={16} className="text-emerald-400" />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// Clip Count Slider Component
const ClipCountSlider: React.FC<{
    value: number;
    onChange: (value: number) => void;
    max: number;
    subscription: any;
}> = ({ value, onChange, max, subscription }) => {
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-white/70">
                    Number of Clips
                </label>
                <div className="text-sm text-white/60">
                    {value} clip{value !== 1 ? 's' : ''}
                </div>
            </div>
            
            <div className="relative">
                <input
                    type="range"
                    min="1"
                    max={max}
                    value={value}
                    onChange={(e) => {
                        const newValue = parseInt(e.target.value);
                        console.log('[ClipCountSlider] Changing from', value, 'to', newValue);
                        onChange(newValue);
                    }}
                    className="
                        w-full h-2 bg-white/10 rounded-full appearance-none cursor-pointer
                        slider-thumb:appearance-none slider-thumb:h-5 slider-thumb:w-5 
                        slider-thumb:rounded-full slider-thumb:bg-gradient-to-r 
                        slider-thumb:from-blue-400 slider-thumb:to-cyan-400
                        slider-thumb:cursor-pointer slider-thumb:shadow-lg
                    "
                    style={{
                        background: `linear-gradient(to right, 
                            rgb(59 130 246 / 0.6) 0%, 
                            rgb(59 130 246 / 0.6) ${(value / max) * 100}%, 
                            rgb(255 255 255 / 0.1) ${(value / max) * 100}%, 
                            rgb(255 255 255 / 0.1) 100%)`
                    }}
                />
                <div className="flex justify-between text-xs text-white/40 mt-2">
                    <span>1</span>
                    <span>{max}</span>
                </div>
            </div>
            
            {subscription?.tierId && (
                <div className="text-xs text-white/50">
                    Your Tier {subscription.tierId} plan allows up to {max} clips
                </div>
            )}
        </div>
    );
};

// VOD Preview Card Component
const VodPreviewCard: React.FC<{
    title?: string;
    thumbnail?: string;
    vodUrl?: string;
    isLoading?: boolean;
}> = ({ title, thumbnail, vodUrl, isLoading }) => {
    if (isLoading) {
        return (
            <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-xl p-4">
                <div className="flex items-start gap-4">
                    <div className="w-24 h-16 bg-white/10 rounded-lg animate-pulse flex-shrink-0" />
                    <div className="flex-1 space-y-2">
                        <div className="h-4 bg-white/10 rounded animate-pulse w-3/4" />
                        <div className="h-3 bg-white/10 rounded animate-pulse w-1/2" />
                        <div className="h-6 bg-white/10 rounded animate-pulse w-20" />
                    </div>
                </div>
            </div>
        );
    }

    if (!title) return null;

    return (
        <div className="bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-xl p-4">
            <div className="flex items-start gap-4">
                {thumbnail ? (
                    <img
                        src={thumbnail}
                        alt="VOD thumbnail"
                        className="w-24 h-16 rounded-lg object-cover flex-shrink-0"
                    />
                ) : (
                    <div className="w-24 h-16 bg-gradient-to-br from-gray-500/20 to-gray-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Play size={20} className="text-white/40" />
                    </div>
                )}
                <div className="flex-1 min-w-0 space-y-2">
                    <h4 className="font-medium text-white/90 line-clamp-2 text-sm leading-relaxed">
                        {title}
                    </h4>
                    {vodUrl && (
                        <p className="text-xs text-white/40 truncate font-mono">
                            {vodUrl}
                        </p>
                    )}
                    <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium bg-green-500/20 text-green-300 border border-green-500/30">
                            <Check size={12} />
                            VOD Ready
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export const ModernVodUploadForm: React.FC<ModernVodUploadFormProps> = ({
    initialSelectedDevice,
    onDeviceChange,
    customLlmPrompt,
    setCustomLlmPrompt,
    clipCount,
    setClipCount,
    customPromptAllowed = false,
    onBeforeUpload,
    onAfterUpload
}) => {
    const { activeSubscription } = useSubscription();
    const { user } = useAuth();

    // Form state
    const [vodUrl, setVodUrl] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isCheckingVod, setIsCheckingVod] = useState(false);
    const [error, setError] = useState('');
    const [vodInfo, setVodInfo] = useState<{ title?: string; thumbnail?: string } | null>(null);
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [showVodModal, setShowVodModal] = useState(false);
    const [userVods, setUserVods] = useState<PlatformVod[]>([]);
    const [isLoadingUserVods, setIsLoadingUserVods] = useState(false);
    const [userVodsError, setUserVodsError] = useState<string | null>(null);

    // Subscription limits based on tierId
    const maxClips = activeSubscription?.tierId === 3 ? 20 : activeSubscription?.tierId === 2 ? 10 : 5;

    // Debug subscription
    // console.log('[ModernVodUploadForm] activeSubscription:', activeSubscription);
    // console.log('[ModernVodUploadForm] clipCount:', clipCount, 'maxClips:', maxClips);

    // URL validation
    const isValidUrl = useMemo(() => {
        if (!vodUrl.trim()) return false;
        const twitchRegex = /^https:\/\/(?:www\.)?twitch\.tv\/videos\/\d+$/;
        const kickRegex = /^https:\/\/(?:www\.)?kick\.com\/[^\/]+\/videos\/[a-f0-9-]+$/;
        return twitchRegex.test(vodUrl) || kickRegex.test(vodUrl);
    }, [vodUrl]);

    // Check VOD info when URL changes
    useEffect(() => {
        const checkVod = async () => {
            if (!isValidUrl) {
                setVodInfo(null);
                return;
            }

            setIsCheckingVod(true);
            setError('');

            try {
                const response = await api.post('/vod/pre-check', { vodUrl });
                setVodInfo({
                    title: response.data.title,
                    thumbnail: response.data.thumbnailUrl
                });
                console.log('[ModernVodUploadForm] VOD info fetched:', response.data);
            } catch (err: any) {
                console.error('[ModernVodUploadForm] VOD check error:', err);
                setError(err.response?.data?.error || 'Failed to check VOD');
                setVodInfo(null);
            } finally {
                setIsCheckingVod(false);
            }
        };

        const debounceTimer = setTimeout(checkVod, 500);
        return () => clearTimeout(debounceTimer);
    }, [vodUrl, isValidUrl]);

    // Fetch user VODs
    const fetchUserVods = async () => {
        if (!user) return;
        
        setIsLoadingUserVods(true);
        setUserVodsError(null);
        
        try {
            const response = await api.get('/vods');
            setUserVods(response.data || []);
        } catch (err: any) {
            console.error('[ModernVodUploadForm] Error fetching user VODs:', err);
            setUserVodsError(err.response?.data?.error || 'Failed to fetch VODs');
        } finally {
            setIsLoadingUserVods(false);
        }
    };

    // Handle VOD modal open
    const handleBrowseVods = () => {
        setShowVodModal(true);
        fetchUserVods();
    };

    // Handle VOD selection from modal
    const handleSelectVod = (vod: PlatformVod) => {
        setVodUrl(vod.url);
        setVodInfo({
            title: vod.title,
            thumbnail: vod.thumbnail_url
        });
        setShowVodModal(false);
    };

    // Submit form
    const handleSubmit = async () => {
        if (!isValidUrl || !vodInfo) return;

        setIsSubmitting(true);
        setError('');

        try {
            const isGpuAttempt = initialSelectedDevice === 'GPU';
            
            // Pre-upload check
            if (onBeforeUpload) {
                const canProceed = await onBeforeUpload(isGpuAttempt);
                if (!canProceed) return;
            }

            // Submit job
            const jobData = {
                vodUrl,
                whisperModel: 'small', // Default model
                whisperDevice: initialSelectedDevice === 'GPU' ? 'cuda' : 'cpu', // Map GPU to cuda, CPU to cpu
                customLlmPrompt: customPromptAllowed ? customLlmPrompt : '',
                clipCount: clipCount.toString(),
                vodTitle: vodInfo.title,
                vodThumbnailUrl: vodInfo.thumbnail
            };

            await api.post('/jobs', jobData);

            // Post-upload callback
            if (onAfterUpload) {
                await onAfterUpload(isGpuAttempt);
            }

            // Reset form
            setVodUrl('');
            setVodInfo(null);
            setError('');

        } catch (err: any) {
            // Try to get error message from different possible fields
            const errorMessage = err.response?.data?.message || 
                               err.response?.data?.error || 
                               err.message || 
                               'Failed to create job';
            setError(errorMessage);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <div className="p-3 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 backdrop-blur-xl border border-blue-400/20">
                        <CloudUpload size={24} className="text-blue-300" />
                    </div>
                    <div>
                        <h2 className="text-xl font-bold text-white">
                            Upload VOD for Processing
                        </h2>
                        <p className="text-white/60 text-sm">
                            Paste a Twitch or Kick VOD link to create AI-powered highlights
                        </p>
                    </div>
                </div>
            </div>

            {/* Main Form */}
            <div className="bg-white/[0.02] backdrop-blur-xl border border-white/5 rounded-2xl p-8 space-y-6">
                {/* VOD URL Input */}
                <div className="space-y-4">
                    <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-white/70">
                            VOD URL
                        </label>
                        <GlassButton
                            variant="secondary"
                            size="sm"
                            onClick={handleBrowseVods}
                        >
                            <ListVideo size={14} />
                            Browse My VODs
                        </GlassButton>
                    </div>
                    
                    <div className="relative">
                        <GlassInput
                            value={vodUrl}
                            onChange={setVodUrl}
                            placeholder="Paste a Twitch or Kick VOD link here..."
                            icon={<ExternalLink size={16} />}
                            error={vodUrl && !isValidUrl ? 'Please enter a valid Twitch or Kick VOD URL' : error}
                        />
                        
                        {/* Platform Icons */}
                        <div className="flex items-center gap-2 mt-3">
                            <span className="text-xs text-white/50">Supported platforms:</span>
                            <div className="flex items-center gap-3">
                                {/* Twitch Icon */}
                                <div className="flex items-center gap-1.5 px-2 py-1 bg-purple-500/10 border border-purple-400/20 rounded-lg">
                                    <img 
                                        src="/assets/twitchicon.svg" 
                                        alt="Twitch" 
                                        className="w-4 h-4"
                                    />
                                    <span className="text-xs text-purple-200">Twitch</span>
                                </div>
                                
                                {/* Kick Icon */}
                                <div className="flex items-center gap-1.5 px-2 py-1 bg-green-500/10 border border-green-400/20 rounded-lg">
                                    <img 
                                        src="/assets/kickicon.svg" 
                                        alt="Kick" 
                                        className="w-4 h-4"
                                    />
                                    <span className="text-xs text-green-200">Kick</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* VOD Preview */}
                {(isCheckingVod || vodInfo) && (
                    <VodPreviewCard
                        title={vodInfo?.title}
                        thumbnail={vodInfo?.thumbnail}
                        vodUrl={vodUrl}
                        isLoading={isCheckingVod}
                    />
                )}

                {/* Device Selection */}
                <DeviceSelector
                    selected={initialSelectedDevice}
                    onChange={onDeviceChange}
                    subscription={activeSubscription}
                />

                {/* Clip Count */}
                <ClipCountSlider
                    value={clipCount}
                    onChange={setClipCount}
                    max={maxClips}
                    subscription={activeSubscription}
                />

                {/* Advanced Settings */}
                <div className="space-y-4">
                    <button
                        onClick={() => setShowAdvanced(!showAdvanced)}
                        className="flex items-center gap-2 text-sm text-white/60 hover:text-white/80 transition-colors"
                    >
                        <Settings size={14} />
                        Advanced Settings
                        <ChevronDown 
                            size={14} 
                            className={`transition-transform ${showAdvanced ? 'rotate-180' : ''}`} 
                        />
                    </button>

                    {showAdvanced && (
                        <div className="space-y-4 pt-4 border-t border-white/10">
                            {/* Custom AI Prompt */}
                            {customPromptAllowed ? (
                                <div className="space-y-3">
                                    <label className="text-sm font-medium text-white/70 flex items-center gap-2">
                                        <Wand2 size={14} />
                                        Custom AI Prompt
                                        <span className="text-xs bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-400/30 text-purple-200 px-2 py-1 rounded-full">
                                            Premium
                                        </span>
                                    </label>
                                    <textarea
                                        value={customLlmPrompt}
                                        onChange={(e) => setCustomLlmPrompt(e.target.value)}
                                        placeholder="Describe what kind of highlights you want the AI to find..."
                                        className="
                                            w-full p-4 bg-white/[0.02] backdrop-blur-xl border border-white/10 
                                            rounded-xl text-white placeholder-white/40 resize-none
                                            focus:outline-none focus:border-white/30 focus:ring-1 focus:ring-white/20
                                        "
                                        rows={3}
                                    />
                                </div>
                            ) : (
                                <div className="p-4 bg-white/[0.02] border border-white/10 rounded-xl">
                                    <div className="flex items-center gap-3">
                                        <Lock size={16} className="text-white/40" />
                                        <div>
                                            <p className="text-sm text-white/70">Custom AI Prompts</p>
                                            <p className="text-xs text-white/50">Available with Tier 2+ subscription</p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Submit Button */}
                <div className="pt-4">
                    <GlassButton
                        onClick={handleSubmit}
                        disabled={!isValidUrl || !vodInfo || isCheckingVod}
                        loading={isSubmitting}
                        variant="primary"
                        size="lg"
                        className="w-full"
                    >
                        {isSubmitting ? (
                            'Creating Job...'
                        ) : (
                            <>
                                <Sparkles size={16} />
                                Start Processing
                            </>
                        )}
                    </GlassButton>
                </div>
            </div>

            {/* Info Card */}
            <div className="bg-gradient-to-r from-blue-500/10 to-cyan-500/10 backdrop-blur-xl border border-blue-400/20 rounded-xl p-6">
                <div className="flex items-start gap-4">
                    <div className="p-2 bg-blue-500/20 rounded-lg">
                        <Info size={16} className="text-blue-300" />
                    </div>
                    <div className="space-y-2">
                        <h4 className="font-medium text-blue-200">Processing Information</h4>
                        <ul className="text-sm text-blue-200/80 space-y-1">
                            <li>• Processing typically takes 5-15 minutes depending on VOD length</li>
                            <li>• GPU processing is 2-3x faster than CPU (Tier 2+ feature)</li>
                            <li>• You'll receive real-time progress updates during processing</li>
                            <li>• Maximum VOD length: {activeSubscription?.tierId === 3 ? '12' : activeSubscription?.tierId === 2 ? '10' : '8'} hours</li>
                        </ul>
                    </div>
                </div>
            </div>

            {/* VOD Selection Modal */}
            {showVodModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
                    <div className="w-full max-w-4xl max-h-[80vh] bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-2xl p-6 overflow-hidden">
                        {/* Modal Header */}
                        <div className="flex items-center justify-between mb-6">
                            <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-blue-500/20">
                                    <ListVideo size={20} className="text-blue-300" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-white">Browse My VODs</h3>
                                    <p className="text-sm text-white/60">Select a VOD from your connected accounts</p>
                                </div>
                            </div>
                            <GlassButton
                                variant="secondary"
                                size="sm"
                                onClick={() => setShowVodModal(false)}
                                className="!p-2"
                            >
                                ✕
                            </GlassButton>
                        </div>

                        {/* Modal Content */}
                        <div className="overflow-y-auto max-h-[calc(80vh-120px)] custom-scrollbar">
                            {isLoadingUserVods ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="flex items-center gap-3">
                                        <Loader2 size={20} className="animate-spin text-blue-400" />
                                        <span className="text-white/70">Loading your VODs...</span>
                                    </div>
                                </div>
                            ) : userVodsError ? (
                                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                                    <AlertCircle size={32} className="text-red-400" />
                                    <div className="text-center">
                                        <p className="text-white/70 mb-2">Failed to load VODs</p>
                                        <p className="text-sm text-white/50">{userVodsError}</p>
                                    </div>
                                    <GlassButton
                                        variant="secondary"
                                        size="sm"
                                        onClick={fetchUserVods}
                                    >
                                        Try Again
                                    </GlassButton>
                                </div>
                            ) : userVods.length === 0 ? (
                                <div className="flex flex-col items-center justify-center py-12 space-y-4">
                                    <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10">
                                        <ListVideo size={32} className="text-white/40" />
                                    </div>
                                    <div className="text-center">
                                        <p className="text-white/70 mb-2">No VODs found</p>
                                        <p className="text-sm text-white/50">Connect your Twitch or Kick account to see your VODs here</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {userVods.map((vod) => (
                                        <div
                                            key={vod.id}
                                            onClick={() => handleSelectVod(vod)}
                                            className="group bg-white/[0.02] backdrop-blur-xl border border-white/10 rounded-xl p-4 cursor-pointer transition-all duration-200 hover:bg-white/[0.04] hover:border-white/20"
                                        >
                                            <div className="flex items-start gap-4">
                                                {vod.thumbnail_url ? (
                                                    <img
                                                        src={vod.thumbnail_url}
                                                        alt="VOD thumbnail"
                                                        className="w-20 h-12 rounded-lg object-cover flex-shrink-0"
                                                    />
                                                ) : (
                                                    <div className="w-20 h-12 bg-gradient-to-br from-gray-500/20 to-gray-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                                                        <Play size={16} className="text-white/40" />
                                                    </div>
                                                )}
                                                <div className="flex-1 min-w-0 space-y-2">
                                                    <h4 className="font-medium text-white/90 line-clamp-2 text-sm group-hover:text-white transition-colors">
                                                        {vod.title}
                                                    </h4>
                                                    <div className="flex items-center gap-3 text-xs text-white/50">
                                                        <div className="flex items-center gap-1">
                                                            <User size={12} />
                                                            {vod.user_name}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Calendar size={12} />
                                                            {new Date(vod.published_at).toLocaleDateString()}
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <Clock size={12} />
                                                            {vod.duration}
                                                        </div>
                                                    </div>
                                                    <div className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-md text-xs font-medium ${
                                                        vod.platform === 'Twitch' 
                                                            ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                                            : 'bg-green-500/20 text-green-300 border border-green-500/30'
                                                    }`}>
                                                        <img 
                                                            src={vod.platform === 'Twitch' ? '/assets/twitchicon.svg' : '/assets/kickicon.svg'}
                                                            alt={vod.platform}
                                                            className="w-3 h-3"
                                                        />
                                                        {vod.platform}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModernVodUploadForm;